import { addToRecord, objectWithoutKeys } from '@ms/yammer-data-support';
import { CampaignState } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { CampaignDeleteFulfilledAction } from '../../domains/campaignDeletion/actions.types';
import { CampaignEditFulfilledAction } from '../../domains/campaignEdit/actions.types';
import { CampaignUnfollowFulfilledAction } from '../../domains/campaignFollowing/unfollowActions.types';
import { CampaignSashAddFulfilledAction } from '../../domains/campaignSash/addSashActions.types';
import { CampaignSashRemoveFulfilledAction } from '../../domains/campaignSash/removeSashActions.types';
import { SessionLoadCurrentUserFulfilledAction } from '../../domains/sessionLoadCurrentUser/actions.types';
import { UserHeaderLoadFulfilledAction } from '../../domains/userHeaderLoad/actions.types';

import { UserSashImageUrlsRecord } from './types';

export type UserSashImageUrlAction =
  | CampaignEditFulfilledAction
  | CampaignDeleteFulfilledAction
  | CampaignSashAddFulfilledAction
  | CampaignSashRemoveFulfilledAction
  | CampaignUnfollowFulfilledAction
  | UserHeaderLoadFulfilledAction
  | SessionLoadCurrentUserFulfilledAction;

const initialState: UserSashImageUrlsRecord = {};

const addUserSash = (
  state: UserSashImageUrlsRecord,
  { userId, entities }: CampaignSashAddFulfilledAction['payload']
) => {
  const { campaignId, sashImageUrl } = entities.userSashes[userId];

  return addToRecord(state, userId, { id: userId, campaignId, sashImageUrl });
};

const removeUserSash = (state: UserSashImageUrlsRecord, userId: string, campaignId: string) => {
  const userSashImageUrl = state[userId];

  if (userSashImageUrl && campaignId === userSashImageUrl.campaignId) {
    return objectWithoutKeys(state, [userId]);
  }

  return state;
};

const addUserSashImageUrl = (
  state: UserSashImageUrlsRecord,
  { id: userId, entities }: UserHeaderLoadFulfilledAction['payload'] | SessionLoadCurrentUserFulfilledAction['payload']
) => {
  if (!entities.userSashImageUrl[userId]) {
    return state;
  }

  const { campaignId, sashImageUrl } = entities.userSashImageUrl[userId];

  return addToRecord(state, userId, {
    id: userId,
    campaignId,
    sashImageUrl,
  });
};

const removeEndCampaignSash = (
  state: UserSashImageUrlsRecord,
  { id: campaignId, userId, entities }: CampaignEditFulfilledAction['payload']
) => {
  const campaignReferences = entities.campaignReferences[campaignId];
  if (campaignReferences && campaignReferences.state === CampaignState.CLOSED) {
    return removeUserSash(state, userId, campaignId);
  }

  return state;
};

const reducer: Reducer<UserSashImageUrlsRecord, UserSashImageUrlAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CAMPAIGN.EDIT_FULFILLED':
      return removeEndCampaignSash(state, action.payload);
    case 'CAMPAIGN.DELETE_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.campaignId);
    case 'CAMPAIGN.SASH.ADD_FULFILLED':
      return addUserSash(state, action.payload);
    case 'CAMPAIGN.SASH.REMOVE_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.campaignId);
    case 'CAMPAIGN.UNFOLLOW_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.id);
    case 'USER_HEADER.LOAD_FULFILLED':
      return addUserSashImageUrl(state, action.payload);
    case 'SESSION.LOAD_CURRENT_USER_FULFILLED':
      return addUserSashImageUrl(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
